import { Suspense, lazy, useCallback, useEffect, useState, type MouseEvent } from "react";
import { Crashed } from "./components/Crashed.tsx";
import { TERMS_VERSION, type Me } from "@ai-rpg/protocol";
import { SIGNED_OUT, getMe } from "./api.ts";
import { Account } from "./components/Account.tsx";
import { Invite } from "./components/Invite.tsx";
import { LOADING } from "./words.ts";
import { PageSettings } from "./components/PageSettings.tsx";
import { Missing } from "./components/Missing.tsx";
import { Lobby } from "./components/Lobby.tsx";
import { Meter } from "./components/Meter.tsx";
import { SignIn } from "./components/SignIn.tsx";
import { Agree } from "./components/Agree.tsx";
import {
  ACCOUNT,
  ACCOUNTS,
  CHARACTERS,
  GUIDE,
  REPORTS,
  REVIEW,
  ROADMAP,
  VAULTS,
  gamePath,
  playPath,
  routeOf,
} from "./route.ts";

// Each page is fetched when it is first opened: someone signing in to play isn't sent the builder and the admin's tabs.
const Accounts = lazy(() => import("./components/Accounts.tsx").then((m) => ({ default: m.Accounts })));
const Characters = lazy(() => import("./components/Characters.tsx").then((m) => ({ default: m.Characters })));
const Guide = lazy(() => import("./components/Guide.tsx").then((m) => ({ default: m.Guide })));
const Roadmap = lazy(() => import("./components/Roadmap.tsx").then((m) => ({ default: m.Roadmap })));
const Vaults = lazy(() => import("./components/Vaults.tsx").then((m) => ({ default: m.Vaults })));
const Reports = lazy(() => import("./components/Reports.tsx").then((m) => ({ default: m.Reports })));
const Review = lazy(() => import("./components/Review.tsx").then((m) => ({ default: m.Review })));
const Terms = lazy(() => import("./components/Terms.tsx").then((m) => ({ default: m.Terms })));
const TableScreen = lazy(() => import("./components/TableScreen.tsx").then((m) => ({ default: m.TableScreen })));
const Gathering = lazy(() => import("./components/Gathering.tsx").then((m) => ({ default: m.Gathering })));
const Join = lazy(() => import("./components/Join.tsx").then((m) => ({ default: m.Join })));

/** What the tab is called on each page (a game names itself). */
const TITLES: Partial<Record<string, string>> = {
  play: "Play",
  characters: "Characters",
  guide: "Guide",
  roadmap: "Roadmap",
  account: "Account",
  accounts: "Accounts",
  reports: "Reports",
  review: "Review",
  vaults: "Vaults",
  terms: "Terms of use",
  missing: "Nothing here",
};

const here = () => window.location.pathname + window.location.search;
const NOBODY: Me = { open: false, account: null };

/** The whole app: a page that breaks says so and offers the way back, and one still being fetched says it is opening. */
export function App() {
  return (
    <Crashed>
      <Suspense
        fallback={
          <main className="loading quiet" role="status">
            {LOADING}
          </main>
        }
      >
        <Pages />
      </Suspense>
    </Crashed>
  );
}

function Pages() {
  const [location, setLocation] = useState(here);
  // Who the server takes this browser for. Null until it has said.
  const [me, setMe] = useState<Me | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const ask = useCallback(
    () =>
      getMe()
        .then(setMe)
        .catch((err: unknown) => setProblem(err instanceof Error ? err.message : String(err))),
    [],
  );
  useEffect(() => {
    void ask();
    const onPop = () => setLocation(here());
    // A session can end under an open page: it ran out, or was ended from somewhere else.
    const onSignedOut = () => setMe(NOBODY);
    window.addEventListener("popstate", onPop);
    window.addEventListener(SIGNED_OUT, onSignedOut);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener(SIGNED_OUT, onSignedOut);
    };
  }, [ask]);
  const go = (to: string) => {
    window.history.pushState(null, "", to);
    setLocation(to);
  };

  const screen = routeOf(location).screen;
  useEffect(() => {
    if (screen === "game") return;
    const word = TITLES[screen];
    document.title = word ? `${word} \u00B7 The Table` : "The Table";
  }, [screen]);

  if (!me) return <main className="signin">{problem ? <p className="problem">{problem}</p> : null}</main>;
  const route = routeOf(location);
  // An invitation is for someone with no account yet; anyone already signed in is simply let in.
  if (route.screen === "invite" && !me.open && !me.account) {
    return (
      <Invite
        code={route.code}
        onJoined={(joined) => {
          // Someone new is shown how it works first.
          go(GUIDE);
          setMe(joined);
        }}
        onSignIn={() => go(playPath())}
      />
    );
  }
  // The terms can be read by anyone, signed in or not.
  if (route.screen === "terms") return <Terms />;
  // The guide and the roadmap are for anyone to read, signed in or not: they say what the game is, and hold
  // nothing of anyone's. Whoever isn't signed in is shown them with the way to sign in beside them.
  if (!me.open && !me.account && (route.screen === "guide" || route.screen === "roadmap")) {
    const link = (to: string, label: string, on: boolean, aside = false) => (
      <a
        className={aside ? "tab tab-aside" : "tab"}
        href={to}
        aria-current={on ? "page" : undefined}
        onClick={(e: MouseEvent) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          go(to);
        }}
      >
        {label}
      </a>
    );
    return (
      <div className="shell">
        <nav className="tabs" aria-label="Sections">
          <span className="tabs-title">The Table</span>
          {link(GUIDE, "Guide", route.screen === "guide")}
          {link(ROADMAP, "Roadmap", route.screen === "roadmap")}
          {link(playPath(), "Sign in", false, true)}
          <PageSettings />
        </nav>
        {route.screen === "guide" ? <Guide page={route.page} go={go} /> : <Roadmap />}
      </div>
    );
  }
  if (!me.open && !me.account) return <SignIn onSignedIn={setMe} />;
  // An account is asked once whether its person is an adult (and for the terms in force) before anything else.
  if (me.account && (!me.account.adultAt || (TERMS_VERSION !== null && me.account.terms !== TERMS_VERSION))) {
    return <Agree name={me.account.username} onAgreed={setMe} onSignedOut={() => setMe(NOBODY)} />;
  }

  if (route.screen === "game") {
    return (
      <TableScreen
        key={route.id}
        id={route.id}
        onLeave={() => go(playPath())}
        onNext={(characterId) => go(playPath(characterId ?? undefined))}
      />
    );
  }
  if (route.screen === "table") {
    return <Gathering id={route.id} code={route.code} onBegun={(id) => go(gamePath(id))} />;
  }
  if (route.screen === "join") {
    return <Join id={route.id} code={route.code} onSeated={(id) => go(gamePath(id))} />;
  }
  const admin = me.account?.role === "admin";

  // A tab is a link, so it can be opened in a new window; a plain click stays in this one.
  const tab = (to: string, label: string, on: boolean, aside = false) => (
    <a
      className={aside ? "tab tab-aside" : "tab"}
      href={to}
      aria-current={on ? "page" : undefined}
      onClick={(e: MouseEvent) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        go(to);
      }}
    >
      {label}
    </a>
  );
  return (
    <div className="shell">
      <nav className="tabs" aria-label="Sections">
        <span className="tabs-title">The Table</span>
        {tab(playPath(), "Play", route.screen === "play")}
        {tab(CHARACTERS, "Characters", route.screen === "characters")}
        {tab(GUIDE, "Guide", route.screen === "guide")}
        {tab(ROADMAP, "Roadmap", route.screen === "roadmap")}
        {admin && tab(ACCOUNTS, "Accounts", route.screen === "accounts")}
        {admin && tab(REPORTS, "Reports", route.screen === "reports")}
        {admin && tab(REVIEW, "Review", route.screen === "review")}
        {admin && tab(VAULTS, "Vaults", route.screen === "vaults")}
        {me.account && (
          <span className="tab-aside tabs-own">
            <Meter />
            {tab(ACCOUNT, me.account.username, route.screen === "account")}
          </span>
        )}
        <PageSettings />
      </nav>
      {route.screen === "account" && me.account ? (
        <Account
          account={me.account}
          usage={me.usage ?? null}
          onSignedOut={() => {
            go(playPath());
            setMe(NOBODY);
          }}
        />
      ) : route.screen === "accounts" && admin && me.account ? (
        <Accounts route={route} go={go} me={me.account.id} onSelfChanged={() => void ask()} />
      ) : route.screen === "guide" ? (
        <Guide page={route.page} go={go} />
      ) : route.screen === "roadmap" ? (
        <Roadmap />
      ) : route.screen === "vaults" && admin ? (
        <Vaults route={route} go={go} />
      ) : route.screen === "reports" && admin ? (
        <Reports route={route} go={go} />
      ) : route.screen === "review" && admin ? (
        <Review route={route} go={go} />
      ) : route.screen === "characters" ? (
        <Characters route={route} go={go} />
      ) : route.screen === "missing" ? (
        <Missing go={go} />
      ) : (
        <Lobby
          key={me.account?.id ?? "open"}
          pick={route.screen === "play" ? route.pick : null}
          go={go}
          onOpen={(id) => go(gamePath(id))}
        />
      )}
    </div>
  );
}
